/*
 * Formula evaluator for calculated attributes.
 *
 * Formulas are evaluated left-to-right (no operator precedence). The calculated
 * data field's DataField.Type selects the evaluator for the complete formula.
 * Each type-specific evaluator resolves operands according to their concrete
 * token type, while API payloads may also include the C# polymorphic
 * `typeDiscriminator` metadata field.
 *
 * Safety note: evaluation must never use eval / Function. All operator dispatch
 * uses a closed switch statement over known token discriminators.
 */

import { DataField } from "@iventis/domain-model/model/dataField";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { DataFieldListItemPropertyType } from "@iventis/domain-model/model/dataFieldListItemPropertyType";
import { FormulaOperator } from "@iventis/domain-model/model/formulaOperator";
import { FormulaToken as DomainFormulaToken } from "@iventis/domain-model/model/formulaToken";
import { NumberFormulaToken as DomainNumberFormulaToken } from "@iventis/domain-model/model/numberFormulaToken";
import { TimeFormulaToken as DomainTimeFormulaToken } from "@iventis/domain-model/model/timeFormulaToken";
import { OperatorFormulaToken as DomainOperatorFormulaToken } from "@iventis/domain-model/model/operatorFormulaToken";
import { DataFieldFormulaToken as DomainDataFieldFormulaToken } from "@iventis/domain-model/model/dataFieldFormulaToken";
import { DataFieldPropertyFormulaToken as DomainDataFieldPropertyFormulaToken } from "@iventis/domain-model/model/dataFieldPropertyFormulaToken";
import { FormulaTokenType } from "@iventis/domain-model/model/formulaTokenType";
import { Units } from "@iventis/domain-model/model/units";
import { convertFromMeters } from "@iventis/utilities";
import { createFormulaEvaluationDiagnostics, FormulaEvaluationDiagnostics } from "./formula-evaluator-diagnostics";
import { getFormulaUnitKind } from "./formula-units";

// eslint-disable-next-line prettier/prettier
export type FormulaTimeString = `${string}:${string}:${string}`;

type FormulaDiscriminator = { typeDiscriminator?: FormulaTokenType };

export type FormulaToken = DomainFormulaToken & FormulaDiscriminator;
export type NumberFormulaToken = DomainNumberFormulaToken & { type: FormulaTokenType.Number } & FormulaDiscriminator;
export type TimeFormulaToken = DomainTimeFormulaToken & { type: FormulaTokenType.Time; value: FormulaTimeString } & FormulaDiscriminator;
export type OperatorFormulaToken = DomainOperatorFormulaToken & { type: FormulaTokenType.Operator } & FormulaDiscriminator;
export type DataFieldFormulaToken = DomainDataFieldFormulaToken & { type: FormulaTokenType.DataField } & FormulaDiscriminator;
export type DataFieldPropertyFormulaToken = DomainDataFieldPropertyFormulaToken & { type: FormulaTokenType.DataFieldProperty } & FormulaDiscriminator;

/* ---------- Type guards ---------- */

export function isNumberFormulaToken(token: FormulaToken): token is NumberFormulaToken {
    return token != null && token.type === FormulaTokenType.Number && typeof (token as { value?: unknown })?.value === "number";
}
export function isTimeFormulaToken(token: FormulaToken): token is TimeFormulaToken {
    return token != null && token.type === FormulaTokenType.Time && typeof (token as { value?: unknown })?.value === "string";
}
export function isOperatorFormulaToken(token: FormulaToken): token is OperatorFormulaToken {
    return token != null && token.type === FormulaTokenType.Operator && (token as { operator?: unknown })?.operator != null;
}
export function isDataFieldFormulaToken(token: FormulaToken): token is DataFieldFormulaToken {
    return token != null && token.type === FormulaTokenType.DataField && typeof (token as { dataFieldId?: unknown })?.dataFieldId === "string";
}
export function isDataFieldPropertyFormulaToken(token: FormulaToken): token is DataFieldPropertyFormulaToken {
    return (
        token != null &&
        token.type === FormulaTokenType.DataFieldProperty &&
        typeof (token as { dataFieldId?: unknown })?.dataFieldId === "string" &&
        typeof (token as { propertyId?: unknown })?.propertyId === "string"
    );
}

/* ---------- Evaluation ---------- */

export type FormulaResult = { dataFieldType: DataFieldType.Number; value: number } | { dataFieldType: DataFieldType.Time; value: FormulaTimeString };

export interface FormulaEvaluationContext {
    /**
     * All data field values keyed by data field id, as stored on
     * `feature.properties` on the frontend. Values are treated defensively; we
     * never assume the concrete type without checking the referenced data
     * field's DataField.Type.
     */
    dataFieldValues: Record<string, unknown>;
    /** Every data field the formula may reference, keyed by id. */
    dataFieldsById: Record<string, DataField>;
    /** Selected list items required by DataFieldProperty tokens, keyed by id. */
    listItemsById: Record<string, DataFieldListItem>;
}

/** Calculated number results are rounded to 2 decimal places, matching the backend formula evaluator */
export const roundCalculatedNumber = (value: number) => parseFloat(value.toFixed(2));

/**
 * Dispatches to the evaluator matching the calculated data field's type. Returns
 * null when the formula cannot be evaluated for any reason — the caller shows an
 * empty value in that case.
 */
export function evaluateFormula(calculatedDataField: DataField, formula: FormulaToken[], context: FormulaEvaluationContext): FormulaResult | null {
    if (formula == null) {
        return null;
    }
    const diagnostics = createFormulaEvaluationDiagnostics(calculatedDataField, formula.length);
    try {
        switch (calculatedDataField.type) {
            case DataFieldType.Number: {
                const value = evaluateNumberFormula(formula, context, diagnostics);
                return value === null ? null : { dataFieldType: DataFieldType.Number, value: roundCalculatedNumber(value) };
            }
            case DataFieldType.Time: {
                const value = evaluateTimeFormula(formula, context, diagnostics);
                return value === null ? null : { dataFieldType: DataFieldType.Time, value };
            }
            default:
                diagnostics.recordUnexpectedIssue({
                    reasonCode: "FORMULA_INVALID_TARGET_TYPE",
                    severity: "warn",
                    evaluator: "dispatch",
                    detail: String(calculatedDataField.type),
                });
                // Extend later with additional supported types.
                return null;
        }
    } catch (error) {
        diagnostics.recordUnexpectedIssue({
            reasonCode: "FORMULA_UNEXPECTED_EXCEPTION",
            severity: "error",
            evaluator: "dispatch",
            detail: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
        });
        return null;
    } finally {
        diagnostics.flushUnexpectedIssues();
    }
}

/* ---------- Number evaluation ---------- */

export function evaluateNumberFormula(formula: FormulaToken[], context: FormulaEvaluationContext, diagnostics?: FormulaEvaluationDiagnostics): number | null {
    if (formula.length === 0) {
        return null;
    }
    let result = resolveNumberOperand(formula[0], context, diagnostics, 0);
    if (result === null) {
        return null;
    }
    for (let index = 1; index < formula.length; index += 2) {
        const operator = formula[index];
        const operandToken = formula[index + 1];
        if (!isOperatorFormulaToken(operator) || operandToken === undefined) {
            diagnostics?.recordUnexpectedIssue({
                reasonCode: "FORMULA_INVALID_TOKEN_SEQUENCE",
                severity: "error",
                evaluator: "number",
                tokenIndex: index,
            });
            return null;
        }
        const operand = resolveNumberOperand(operandToken, context, diagnostics, index + 1);
        if (operand === null) {
            return null;
        }
        switch (operator.operator) {
            case FormulaOperator.Add:
                result += operand;
                break;
            case FormulaOperator.Subtract:
                result -= operand;
                break;
            case FormulaOperator.Multiply:
                result *= operand;
                break;
            case FormulaOperator.Divide:
                if (operand === 0) {
                    diagnostics?.recordUnexpectedIssue({
                        reasonCode: "FORMULA_DIVISION_BY_ZERO",
                        severity: "warn",
                        evaluator: "number",
                        tokenIndex: index,
                        operator: operator.operator,
                    });
                    return null;
                }
                result /= operand;
                break;
            default:
                diagnostics?.recordUnexpectedIssue({
                    reasonCode: "FORMULA_UNKNOWN_NUMBER_OPERATOR",
                    severity: "error",
                    evaluator: "number",
                    tokenIndex: index,
                    operator: operator.operator,
                });
                return null;
        }
        if (!Number.isFinite(result)) {
            return null;
        }
    }
    return result;
}

function resolveNumberOperand(token: FormulaToken, context: FormulaEvaluationContext, diagnostics?: FormulaEvaluationDiagnostics, tokenIndex?: number): number | null {
    if (isNumberFormulaToken(token)) {
        return token.value;
    }
    if (isDataFieldFormulaToken(token)) {
        return resolveNumberDataFieldValue(token.dataFieldId, token.unit, context, diagnostics, tokenIndex);
    }
    if (isDataFieldPropertyFormulaToken(token)) {
        return resolveNumberDataFieldPropertyValue(token.dataFieldId, token.propertyId, context, diagnostics, tokenIndex);
    }
    diagnostics?.recordUnexpectedIssue({
        reasonCode: "FORMULA_UNSUPPORTED_NUMBER_OPERAND_TOKEN",
        severity: "error",
        evaluator: "number",
        tokenIndex,
        tokenType: token?.type,
    });
    return null;
}

function resolveNumberDataFieldValue(
    dataFieldId: string,
    unit: Units | null | undefined,
    context: FormulaEvaluationContext,
    diagnostics?: FormulaEvaluationDiagnostics,
    tokenIndex?: number
): number | null {
    const referencedDataField = context.dataFieldsById[dataFieldId];
    if (referencedDataField == null) {
        diagnostics?.recordUnexpectedIssue({
            reasonCode: "FORMULA_MISSING_REFERENCED_FIELD",
            severity: "warn",
            evaluator: "number",
            tokenIndex,
            dataFieldId,
        });
        return null;
    }
    // Only allow evaluation against fields whose native type is Number. Do not
    // permit blanket Number() coercion of arbitrary types.
    if (referencedDataField.type !== DataFieldType.Number) {
        diagnostics?.recordUnexpectedIssue({
            reasonCode: "FORMULA_REFERENCED_FIELD_TYPE_MISMATCH",
            severity: "warn",
            evaluator: "number",
            tokenIndex,
            dataFieldId,
            detail: `expected:${DataFieldType.Number}|actual:${String(referencedDataField.type)}`,
        });
        return null;
    }
    const rawValue = context.dataFieldValues[dataFieldId];
    const parsedValue = parseNumberValue(rawValue);
    if (parsedValue === null && isMissingNumberValue(rawValue)) {
        diagnostics?.recordUnexpectedIssue({
            reasonCode: "FORMULA_MISSING_REFERENCED_VALUE",
            severity: "warn",
            evaluator: "number",
            tokenIndex,
            dataFieldId,
        });
    }
    // Area and length are stored in metres, so convert them to the unit selected on the token
    const unitKind = getFormulaUnitKind(referencedDataField);
    return parsedValue === null || unitKind === null ? parsedValue : convertFromMeters(parsedValue, unit, unitKind);
}

function resolveNumberDataFieldPropertyValue(
    dataFieldId: string,
    propertyId: string,
    context: FormulaEvaluationContext,
    diagnostics?: FormulaEvaluationDiagnostics,
    tokenIndex?: number
): number | null {
    const parentDataField = context.dataFieldsById[dataFieldId];
    if (parentDataField == null || parentDataField.type !== DataFieldType.List) {
        diagnostics?.recordUnexpectedIssue({
            reasonCode: "FORMULA_LIST_PARENT_FIELD_INVALID",
            severity: "warn",
            evaluator: "number",
            tokenIndex,
            dataFieldId,
            detail: parentDataField == null ? "missing" : `expected:${DataFieldType.List}|actual:${String(parentDataField.type)}`,
        });
        return null;
    }
    const property = parentDataField.listItemProperties?.find((p) => p.id === propertyId);
    if (property == null || property.type !== DataFieldListItemPropertyType.Number) {
        diagnostics?.recordUnexpectedIssue({
            reasonCode: "FORMULA_LIST_PROPERTY_INVALID",
            severity: "warn",
            evaluator: "number",
            tokenIndex,
            dataFieldId,
            propertyId,
            detail: property == null ? "missing" : `expected:${DataFieldListItemPropertyType.Number}|actual:${String(property.type)}`,
        });
        return null;
    }
    const selectedListItemId = context.dataFieldValues[dataFieldId];
    if (typeof selectedListItemId !== "string" || selectedListItemId.trim() === "") {
        diagnostics?.recordUnexpectedIssue({
            reasonCode: "FORMULA_MISSING_SELECTED_LIST_ITEM",
            severity: "warn",
            evaluator: "number",
            tokenIndex,
            dataFieldId,
        });
        return null;
    }
    const listItem = context.listItemsById[selectedListItemId];
    if (listItem == null) {
        diagnostics?.recordUnexpectedIssue({
            reasonCode: "FORMULA_SELECTED_LIST_ITEM_MISSING",
            severity: "warn",
            evaluator: "number",
            tokenIndex,
            dataFieldId,
            detail: selectedListItemId,
        });
        return null;
    }
    const propertyValue = listItem.propertyValues?.find((v) => v.propertyId === propertyId);
    if (propertyValue == null) {
        diagnostics?.recordUnexpectedIssue({
            reasonCode: "FORMULA_MISSING_LIST_PROPERTY_VALUE",
            severity: "warn",
            evaluator: "number",
            tokenIndex,
            dataFieldId,
            propertyId,
        });
        return null;
    }
    const rawPropertyValue = propertyValue.number ?? propertyValue.text;
    const parsedValue = parseNumberValue(rawPropertyValue);
    if (parsedValue === null && isMissingNumberValue(rawPropertyValue)) {
        diagnostics?.recordUnexpectedIssue({
            reasonCode: "FORMULA_MISSING_LIST_PROPERTY_VALUE",
            severity: "warn",
            evaluator: "number",
            tokenIndex,
            dataFieldId,
            propertyId,
        });
    }
    return parsedValue;
}

function isMissingNumberValue(rawValue: unknown): boolean {
    return rawValue === null || rawValue === undefined || (typeof rawValue === "string" && rawValue.trim() === "");
}

function parseNumberValue(rawValue: unknown): number | null {
    if (rawValue === null || rawValue === undefined || rawValue === "") {
        return null;
    }
    if (typeof rawValue === "number") {
        return Number.isFinite(rawValue) ? rawValue : null;
    }
    if (typeof rawValue === "string") {
        const trimmed = rawValue.trim();
        if (trimmed === "") {
            return null;
        }
        const parsed = Number(trimmed);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

/* ---------- Time evaluation ---------- */

const SECONDS_PER_DAY = 24 * 60 * 60;

export function evaluateTimeFormula(formula: FormulaToken[], context: FormulaEvaluationContext, diagnostics?: FormulaEvaluationDiagnostics): FormulaTimeString | null {
    if (formula.length === 0) {
        return null;
    }
    let resultInSeconds = resolveTimeOperand(formula[0], context, diagnostics, 0);
    if (resultInSeconds === null) {
        return null;
    }
    for (let index = 1; index < formula.length; index += 2) {
        const operator = formula[index];
        const operandToken = formula[index + 1];
        if (!isOperatorFormulaToken(operator) || operandToken === undefined) {
            diagnostics?.recordUnexpectedIssue({
                reasonCode: "FORMULA_INVALID_TOKEN_SEQUENCE",
                severity: "error",
                evaluator: "time",
                tokenIndex: index,
            });
            return null;
        }
        const operandInSeconds = resolveTimeOperand(operandToken, context, diagnostics, index + 1);
        if (operandInSeconds === null) {
            return null;
        }
        switch (operator.operator) {
            case FormulaOperator.Add:
                resultInSeconds += operandInSeconds;
                break;
            case FormulaOperator.Subtract:
                resultInSeconds -= operandInSeconds;
                break;
            default:
                // Multiplication/division are not supported for Time formulas.
                diagnostics?.recordUnexpectedIssue({
                    reasonCode: "FORMULA_UNSUPPORTED_TIME_OPERATOR",
                    severity: "error",
                    evaluator: "time",
                    tokenIndex: index,
                    operator: operator.operator,
                });
                return null;
        }
    }
    return formatSecondsAsTime(normaliseTimeSeconds(resultInSeconds));
}

function resolveTimeOperand(token: FormulaToken, context: FormulaEvaluationContext, diagnostics?: FormulaEvaluationDiagnostics, tokenIndex?: number): number | null {
    if (isTimeFormulaToken(token)) {
        const parsed = parseTimeToSeconds(token.value);
        if (parsed === null) {
            diagnostics?.recordUnexpectedIssue({
                reasonCode: "FORMULA_INVALID_TIME_LITERAL_TOKEN",
                severity: "error",
                evaluator: "time",
                tokenIndex,
                detail: token.value,
            });
        }
        return parsed;
    }
    if (isDataFieldFormulaToken(token)) {
        return resolveTimeDataFieldValue(token.dataFieldId, context, diagnostics, tokenIndex);
    }
    // Data-field property tokens are not supported for Time formulas.
    diagnostics?.recordUnexpectedIssue({
        reasonCode: "FORMULA_UNSUPPORTED_TIME_OPERAND_TOKEN",
        severity: "error",
        evaluator: "time",
        tokenIndex,
        tokenType: token?.type,
    });
    return null;
}

function resolveTimeDataFieldValue(dataFieldId: string, context: FormulaEvaluationContext, diagnostics?: FormulaEvaluationDiagnostics, tokenIndex?: number): number | null {
    const referencedDataField = context.dataFieldsById[dataFieldId];
    if (referencedDataField == null) {
        diagnostics?.recordUnexpectedIssue({
            reasonCode: "FORMULA_MISSING_REFERENCED_FIELD",
            severity: "warn",
            evaluator: "time",
            tokenIndex,
            dataFieldId,
        });
        return null;
    }
    if (referencedDataField.type !== DataFieldType.Time) {
        diagnostics?.recordUnexpectedIssue({
            reasonCode: "FORMULA_REFERENCED_FIELD_TYPE_MISMATCH",
            severity: "warn",
            evaluator: "time",
            tokenIndex,
            dataFieldId,
            detail: `expected:${DataFieldType.Time}|actual:${String(referencedDataField.type)}`,
        });
        return null;
    }
    const rawValue = context.dataFieldValues[dataFieldId];

    // Treat unset/missing time values as 00:00:00.
    if (rawValue === null || rawValue === undefined) {
        return 0;
    }
    if (typeof rawValue !== "string") {
        diagnostics?.recordUnexpectedIssue({
            reasonCode: "FORMULA_MISSING_REFERENCED_VALUE",
            severity: "warn",
            evaluator: "time",
            tokenIndex,
            dataFieldId,
        });
        return null;
    }
    if (rawValue.trim() === "") {
        return 0;
    }

    return parseTimeToSeconds(rawValue);
}

/** Normalises a signed number of seconds into `[0, 24h)`. */
export function normaliseTimeSeconds(value: number): number {
    if (!Number.isFinite(value)) {
        return 0;
    }
    return ((value % SECONDS_PER_DAY) + SECONDS_PER_DAY) % SECONDS_PER_DAY;
}

const TIME_REGEX = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

/** Returns the number of seconds represented by the time string, or null if invalid. */
export function parseTimeToSeconds(value: string): number | null {
    if (typeof value !== "string") {
        return null;
    }
    const match = TIME_REGEX.exec(value.trim());
    if (match == null) {
        return null;
    }
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = match[3] == null ? 0 : Number(match[3]);
    if (hours > 23 || minutes > 59 || seconds > 59) {
        return null;
    }
    return hours * 3600 + minutes * 60 + seconds;
}

/** Formats a whole number of seconds within a day as `HH:MM:SS`. */
export function formatSecondsAsTime(totalSeconds: number): FormulaTimeString {
    const wholeSeconds = Math.round(totalSeconds);
    const hours = Math.floor(wholeSeconds / 3600);
    const minutes = Math.floor((wholeSeconds % 3600) / 60);
    const seconds = wholeSeconds % 60;
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` as FormulaTimeString;
}

/* ---------- Convenience helpers ---------- */

/** The set of data field types for which calculated values are supported. */
export function supportsCalculatedValue(dataFieldType: DataFieldType): boolean {
    return dataFieldType === DataFieldType.Number || dataFieldType === DataFieldType.Time;
}
